import React, { useContext, useRef, useState, useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import {
	Box,
	Card,
	CardActionArea,
	CardContent,
	Typography,
	IconButton,
	CircularProgress,
	Grid,
	Stack,
	Autocomplete,
	TextField,
	Link,
	Button,
} from "@mui/material";
import { Link as RouterLink } from "react-router-dom";
import { ArrowBackIosNew, ArrowForwardIos } from "@mui/icons-material";
import { GlobalContext } from "../../GlobalContext";
import { obtenerDatos } from "../../api/config";
import { CoverageCard, HolguraCard, StackedBarChartMUI } from "../Charts.jsx/ChartsMUI";
import { ModalChartHome } from "./PopUp";

// Función que retorna el listado completo de meses con sus traducciones
const getMesesNombres = (t) => [
	{ id: 1, nombre: t("aux.months.Jan") },
	{ id: 2, nombre: t("aux.months.Feb") },
	{ id: 3, nombre: t("aux.months.Mar") },
	{ id: 4, nombre: t("aux.months.Apr") },
	{ id: 5, nombre: t("aux.months.May") },
	{ id: 6, nombre: t("aux.months.Jun") },
	{ id: 7, nombre: t("aux.months.Jul") },
	{ id: 8, nombre: t("aux.months.Aug") },
	{ id: 9, nombre: t("aux.months.Sep") },
	{ id: 10, nombre: t("aux.months.Oct") },
	{ id: 11, nombre: t("aux.months.Nov") },
	{ id: 12, nombre: t("aux.months.Dec") },
];

const Inicio = () => {
	const { user, authenticated, site_id, viewPermissions, isGranted } = useContext(GlobalContext);
	const { t } = useTranslation();
	const [openPopup, setOpenPopup] = useState(false);
	// Estados para la data y la carga
	const [loading, setLoading] = useState(false);
	const [data, setData] = useState(null);

	// Usamos el listado completo de meses (con useMemo para no recalcularlo)
	const mes = useMemo(() => getMesesNombres(t), [t]);
	// 2) Periodos disponibles (YYYY-MM)

	useEffect(() => {
		setData(null);
		setSelectedPeriodo(null);
	}, [site_id]);

	const periodos = useMemo(() => {
		if (!data) return [];
		return Object.keys(data)
			.map((key) => {
				const [yearStr, monthStr] = key.split("-");
				const year = Number(yearStr);
				const month = Number(monthStr);
				const nombre = mes.find((m) => m.id === month)?.nombre || monthStr;
				return {
					id: key, // p.ej. "2025-03"
					year,
					month,
					label: `${nombre} ${year}`,
				};
			})
			.sort((a, b) => (a.year !== b.year ? a.year - b.year : a.month - b.month));
	}, [data, mes]);

	const [selectedPeriodo, setSelectedPeriodo] = useState(null);

	// Al cargar por primera vez, preselecciona el mes-año actual si existe:
	useEffect(() => {
		if (!selectedPeriodo && periodos.length > 0) {
			const hoy = new Date();
			const claveHoy = `${hoy.getFullYear()}-${String(hoy.getMonth() + 1).padStart(2, "0")}`;
			const defecto = periodos.find((p) => p.id === claveHoy) || periodos[0];
			setSelectedPeriodo(defecto);
		}
	}, [periodos, selectedPeriodo]);

	const scrollContainerRef = useRef(null);

	const handleScrollLeft = () => {
		if (scrollContainerRef.current) {
			scrollContainerRef.current.scrollBy({
				left: -200,
				behavior: "smooth",
			});
		}
	};

	const handleScrollRight = () => {
		if (scrollContainerRef.current) {
			scrollContainerRef.current.scrollBy({
				left: 200,
				behavior: "smooth",
			});
		}
	};

	// Llamada al endpoint y obtención de la data
	useEffect(() => {
		const fetchData = async () => {
			if (authenticated && site_id && user) {
				setLoading(true);
				try {
					const resultado = await obtenerDatos(`/outputs/get_approved_output?site_id=${site_id}`);

					setData(resultado);
				} catch (error) {
					console.error("Error en la llamada al endpoint:", error);
				} finally {
					setLoading(false);
				}
			}
		};
		fetchData();
	}, [authenticated, site_id, user]);

	const hasDataForSelectedPeriodo =
		data && selectedPeriodo ? Boolean(data[selectedPeriodo.id]) : false;
	// Secciones del carrusel (con “Configuración” y sub-secciones como botones)
	const sections = useMemo(() => {
		const canViewDashboard = isGranted("Dashboard", "Ver el módulo de dashboard");
		const canViewReview = isGranted("Revisión", "Ver el módulo de revisión");
		const canEditSite = isGranted("Configuración", "Editar Site");
		const canEditContract = isGranted("Configuración", "Editar Contrato");

		const configSubs = [];
		if (canEditSite) {
			configSubs.push({
				title: t("home.section.configuration.site_config.title"),
				route: "/config/configeditsite",
			});
		}
		if (canEditContract) {
			configSubs.push({
				title: t("home.section.configuration.contract_config.title"),
				route: "/config/configedit",
			});
		}

		const list = [];
		if (viewPermissions.config && configSubs.length > 0) {
			list.push({
				title: t("home.section.configuration.title"),
				description: t("home.section.configuration.description"),
				subSections: configSubs,
			});
		}

		if (viewPermissions.execute) {
			list.push({
				title: t("home.section.sim_masive.title"),
				description: t("home.section.sim_masive.description"),
				route: "/execute",
			});
		}

		if (canViewReview) {
			list.push({
				title: t("home.section.review.title"),
				description: t("home.section.review.description"),
				route: "/review",
			});
		}

		if (viewPermissions.simulation) {
			list.push({
				title: t("home.section.simulation.title"),
				description: t("home.section.simulation.description"),
				route: "/simulation",
			});
		}

		if (canViewDashboard) {
			list.push({
				title: t("home.section.dashboard.title"),
				description: t("home.section.dashboard.description"),
				route: "/dashboard",
			});
		}

		if (viewPermissions.audit) {
			list.push({
				title: t("home.section.audit.title"),
				description: t("home.section.audit.description"),
				route: "/audit",
			});
		}

		if (viewPermissions.upload) {
			list.push({
				title: t("home.section.upload.title"),
				description: t("home.section.upload.description"),
				route: "/upload",
			});
		}

		return list.slice(0, 4);
	}, [t, viewPermissions, isGranted]);
	const hasAnySection = sections.length > 0;

	return (
		<Box
			sx={{ display: "flex", flexDirection: "column", alignItems: "center", py: 4, height: "86vh" }}
		>
			{/* Encabezado */}
			<Box sx={{ textAlign: "center", mb: 3 }}>
				<Typography variant="h4" component="h1">
					{t("aux.welcome")} {user}
				</Typography>
				<Typography variant="h6" component="h2" sx={{ color: "text.secondary" }}>
					{t("home.subtitle")}
				</Typography>
			</Box>

			{/* Carrusel con flechas */}
			{hasAnySection && (
				<Box sx={{ display: "flex", alignItems: "center", width: "75%", mb: 4, gap: 2 }}>
					<IconButton onClick={handleScrollLeft}>
						<ArrowBackIosNew />
					</IconButton>

					<Box
						ref={scrollContainerRef}
						sx={{
							display: "flex",
							flexWrap: "nowrap",
							gap: 1,
							overflowX: "hidden",
							scrollBehavior: "smooth",
							flex: 1,
						}}
					>
						{sections.map((section, index) => {
							// Si la sección tiene subSections, mostramos la tarjeta “padre” + sub-botones
							if (section.subSections && section.subSections.length > 0) {
								return (
									<Card
										key={index}
										variant="outlined"
										sx={{ maxWidth: 280, flex: "0 0 auto", borderRadius: 2 }}
									>
										<CardContent>
											<Typography variant="h6" gutterBottom>
												{section.title}
											</Typography>
											<Typography variant="body2" color="text.secondary" gutterBottom>
												{section.description}
											</Typography>

											{/* Sub-secciones en forma de botones pequeños */}
											<Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
												{section.subSections.map((subSection, subIndex) => (
													<Button
														sx={{ width: "48%" }}
														key={subIndex}
														variant="outlined"
														component={RouterLink}
														to={subSection.route}
														size="small"
													>
														{subSection.title}
													</Button>
												))}
											</Box>
										</CardContent>
									</Card>
								);
							} else {
								return (
									<Card
										key={index}
										variant="outlined"
										sx={{ maxWidth: 280, flex: "0 0 auto", borderRadius: 2 }}
									>
										<CardActionArea component={RouterLink} to={section.route}>
											<CardContent>
												<Typography variant="h6">{section.title}</Typography>
												<Typography variant="body2" color="text.secondary">
													{section.description}
												</Typography>
											</CardContent>
										</CardActionArea>
									</Card>
								);
							}
						})}
					</Box>

					<IconButton onClick={handleScrollRight}>
						<ArrowForwardIos />
					</IconButton>
				</Box>
			)}

			{/* Título para la sección de resultados */}
			<Box sx={{ textAlign: "center", mb: 1 }}>
				<Typography variant="h6" component="div">
					{t("home.results.title")}
				</Typography>
			</Box>

			{/* Contenedor para gráficos y filtro */}
			<Box
				sx={{
					width: "75%",
					mb: 2,
				}}
			>
				{loading ? (
					<CircularProgress />
				) : data ? (
					<Grid container spacing={2} sx={{ height: "100%" }}>
						<Grid item xs={12}>
							<Autocomplete
								sx={{ mx: 1 }}
								size="small"
								options={periodos}
								value={selectedPeriodo}
								onChange={(e, v) => setSelectedPeriodo(v)}
								getOptionLabel={(opt) => opt.label}
								isOptionEqualToValue={(opt, val) => opt.id === val.id}
								renderInput={(params) => (
									<TextField
										{...params}
										label={t("aux.filter_per_month")}
										variant="outlined"
										sx={{ "& .MuiInputBase-root": { padding: "4px", height: "36px" } }}
									/>
								)}
							/>
						</Grid>

						{/* Si no hay data para el mes seleccionado, mostramos un mensaje */}
						{!hasDataForSelectedPeriodo ? (
							<Grid item xs={12}>
								<Typography variant="body1" align="center">
									{t("home.results.no_data")}
									{selectedPeriodo ? selectedPeriodo.label : t("home.results.no_data_default")}.
								</Typography>
							</Grid>
						) : (
							<>
								{/* Columna izquierda: Coverage y Holgura */}
								<Grid item xs={4}>
									<Stack spacing={2}>
										<CoverageCard
											data={data}
											selectedPeriods={selectedPeriodo ? [selectedPeriodo] : []}
											fullHeight
										/>
										<HolguraCard
											data={data}
											selectedPeriods={selectedPeriodo ? [selectedPeriodo] : []}
											fullHeight
										/>
									</Stack>
								</Grid>

								{/* Columna derecha: Gráfico de barras apiladas */}
								<Grid item xs={8}>
									<StackedBarChartMUI
										data={data}
										selectedPeriods={selectedPeriodo ? [selectedPeriodo] : []}
									/>
								</Grid>

								<Grid item xs={12} sx={{ display: "flex", justifyContent: "center" }}>
									<Link
										component="button"
										onClick={() => setOpenPopup(true)}
										sx={{ textDecoration: "underline", cursor: "pointer" }}
									>
										{t("home.results.button.more")}
									</Link>
								</Grid>
							</>
						)}
					</Grid>
				) : (
					<Typography variant="body1" component="div">
						{t("home.results.no_results")}
					</Typography>
				)}
			</Box>

			{/* Pop-up con más detalles (modal) */}
			<ModalChartHome
				open={openPopup}
				onClose={() => setOpenPopup(false)}
				data={data} // La misma data usada en los gráficos
				selectedPeriod={selectedPeriodo} // El mes seleccionado
			/>
		</Box>
	);
};

export { Inicio };
